import { Spinner } from '@change/ds-core-ui';

import { getFacebookLocale } from '@change-corgi/config/facebook';
import { useKnownAvailableParams } from '@change-corgi/core-react/router';

import { withPrefetchedData } from '#src/shared/prefetch';

import type {
	PetitionUpdatePageData,
	PetitionUpdatePagePrefetchedDataProps,
} from '#src/app/pages/petitionUpdate/details/pageContext';
import {
	PetitionUpdatePageContextProvider,
	prefetchPageContext,
} from '#src/app/pages/petitionUpdate/details/pageContext';
import { addSchemeToUrl } from '#src/app/shared/utils/assets';
import { isLoaded } from '#src/app/shared/utils/async';
import { stripEmojis } from '#src/app/shared/utils/string';

import { Layout } from '../layout';

import { PetitionUpdateErrorHandler } from './ErrorHandler';

const DEFAULT_IMAGE = '/images/default-petition.gif';
const DEFAULT_IMAGE_WIDTH = 1120;
const DEFAULT_IMAGE_HEIGHT = 630;

function resolveUpdatePhoto(
	update: PetitionUpdatePageData,
): { url: string; width: number; height: number } | undefined {
	if (update.__typename === 'FeaturedNewsPetitionUpdate') {
		const { photo } = update.newsSource;
		if (photo) {
			return { url: addSchemeToUrl(photo.url), width: photo.width, height: photo.height };
		}
	} else {
		if (update.photo?.petitionLarge) {
			const { url, width, height } = update.photo.petitionLarge;
			return { url: addSchemeToUrl(url), width, height };
		}
		const { thumbnailUrl, thumbnailWidth, thumbnailHeight } = update.embeddedMedia || {};
		if (thumbnailUrl && thumbnailWidth && thumbnailHeight) {
			return { url: addSchemeToUrl(thumbnailUrl), width: thumbnailWidth, height: thumbnailHeight };
		}
	}
	return undefined;
}

export const PetitionUpdate = withPrefetchedData(
	function PetitionUpdate({ prefetchedData }: PetitionUpdatePagePrefetchedDataProps) {
		const { petitionSlugOrId, updateId } = useKnownAvailableParams<{ petitionSlugOrId: string; updateId: string }>();

		return (
			<PetitionUpdatePageContextProvider
				petitionSlugOrId={petitionSlugOrId}
				updateId={updateId}
				prefetchedData={prefetchedData}
				errorHandler={PetitionUpdateErrorHandler}
				loadingFallback={<Spinner className="mx-auto my-64" />}
			>
				<Layout />
			</PetitionUpdatePageContextProvider>
		);
	},
	{
		prefetchName: __MODULE_HASH__,
		prefetchDependencies: [Layout],
		prefetchData: async (context) =>
			// oxlint-disable-next-line typescript-eslint/no-non-null-assertion
			prefetchPageContext(context.params.petitionSlugOrId!, context.params.updateId!, context),
		pageTitle: ({ prefetchedData, i18n }) => {
			if (!isLoaded(prefetchedData.pageData)) {
				return undefined;
			}
			const { title } = prefetchedData.pageData.pageData.update;
			return [i18n.translate('fe.pages.petition_update.title'), title, 'Change.org'].join(' · ');
		},

		pageMetadata: ({ prefetchedData, getStaticAssetUrl }) => {
			if (!isLoaded(prefetchedData.pageData)) {
				return {};
			}
			const { update } = prefetchedData.pageData.pageData;
			const { title, descriptionStripped, petition } = update;
			const fbLocale = getFacebookLocale(petition.originalLocale.localeCode) || getFacebookLocale('en-US');

			const updatePhoto = resolveUpdatePhoto(update);
			let photo = updatePhoto;
			if (!photo && petition.photo?.petitionLarge) {
				const { url, width, height } = petition.photo.petitionLarge;
				photo = { url: addSchemeToUrl(url), width, height };
			}

			return {
				twitterCard: 'summary_large_image',
				ogLocale: fbLocale,
				ogTitle: title,
				ogDescription: stripEmojis(descriptionStripped),
				twitterImageAlt: title,
				...(photo
					? {
							ogImage: photo.url,
							ogImageSecureUrl: photo.url,
							ogImageWidth: photo.width,
							ogImageHeight: photo.height,
							twitterImage: photo.url,
						}
					: {
							ogImage: getStaticAssetUrl(DEFAULT_IMAGE),
							ogImageSecureUrl: getStaticAssetUrl(DEFAULT_IMAGE),
							ogImageWidth: DEFAULT_IMAGE_WIDTH,
							ogImageHeight: DEFAULT_IMAGE_HEIGHT,
							twitterImage: getStaticAssetUrl(DEFAULT_IMAGE),
						}),
			};
		},
	},
);
